import React, { type ReactNode, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as ConfluenceSearchResultType } from '@atlassian/search-client/common/types';
import { getConfluenceUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getConfluenceEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { getSearchResultType } from '../../../../../../common/ui/search-result-type';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import { useFeatures } from '../../../../../../controllers/store/bootstrap';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchConfluenceSpace_AtlassianSearchPage$key } from './__generated__/searchConfluenceSpace_AtlassianSearchPage.graphql';

export const isSearchConfluenceSpace = (
	node?: any,
): node is searchConfluenceSpace_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchConfluenceSpace';
};

export const SearchConfluenceSpace = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	searchQuery: query,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchResultProps<searchConfluenceSpace_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchConfluenceSpace_AtlassianSearchPage on SearchConfluenceSpace {
				__typename
				id
				description
				title
				type
				url
				iconUrl
				lastModified
			}
		`,
		node,
	);

	const [features] = useFeatures();

	const isSideBySideEnabled = features?.sideBySide?.enabled;

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		let containerId: string | null = null;
		return {
			id: data.id,
			containerId,
			spaceId: containerId,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			isSideBySideEnabled,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data, isSideBySideEnabled, cloudId, selectedSites]);

	const metadataParts: [string, ReactNode][] = [
		['type', getSearchResultType(data.type as ConfluenceSearchResultType)],
		['last-modified', data.lastModified && <LastModified lastModified={data.lastModified} />],
	];

	const icon = getConfluenceEntityIcon({
		productConfigs,
		iconUrl: data.iconUrl,
		type: data.type,
		size: 'medium',
	});

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					product={ProductKeys.Confluence}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={getConfluenceUrlWithAnalyticsQueryParams({
						url: data.url,
						query,
						searchSessionId,
					})}
					title={enrichExcerpt(data.title)}
					icon={icon}
					headerMetadataParts={metadataParts}
					description={<DescriptionBody description={processExcerpt(data.description)} />}
					cloudId={cloudId}
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};
